// The pseudo-planet simulation behind the playground widget: a synthetic 120 BPM
// bench signal fed through the article's EXACT beat detector, plus the cheap 2D
// "pseudo-shader" draw (radial-gradient sphere + one energy-displaced ring). No
// WebGL, no allocations in the render loop.
//
// Extracted as pure functions so the sim math and the draw core are unit-testable
// without a real canvas 2D context (jsdom has none): every pixel op goes through
// the passed `ctx`, so a recording mock verifies the draw. Deterministic — an
// instrument doesn't roll dice; the same clock + params always draw the same bars.

// Deterministic wobble in [0, 1); wob(0) === 0.
export const wob = (i) => {
  const x = Math.sin(i * 12.9898) * 43758.5453
  return x - Math.floor(x)
}

const hx = (h) => [parseInt(h.slice(1, 3), 16), parseInt(h.slice(3, 5), 16), parseInt(h.slice(5, 7), 16)]
export const mix = (a, b, t) => `rgb(${a.map((v, i) => Math.round(v + (b[i] - v) * t)).join(",")})`
export const EARTH = ["#5FD3F3", "#1E6E86", "#0E1A20"].map(hx)
export const LAVA = ["#FFC08A", "#FF4D00", "#571A00"].map(hx)

// Parameter bounds — the one-liner clamps to these (the faders are already bounded
// by their input min/max attributes).
export const THRESHOLD_MIN = 1
export const THRESHOLD_MAX = 2
export const BAND_MIN = 8
export const BAND_MAX = 48
export const DEFAULT_THRESHOLD = 1.4
export const DEFAULT_BAND = 24

// Fresh sim state. rolling seeds at 80 so the first frames don't false-trigger.
export function createState() {
  return { threshold: DEFAULT_THRESHOLD, band: DEFAULT_BAND, lava: 0, rolling: 80, t: 0 }
}

// 120 BPM kick over a breathing bass line. Widening band past 24 lets synthetic
// hi-hats leak into the energy sum (exactly the article's failure mode);
// narrowing it starves the kick.
export function energy(t, band) {
  return (
    (58 + 26 * Math.sin(t / 1300)) +
    Math.exp(-((t % 500) / 500) * 9) * 185 * Math.min(1, band / 24) +
    Math.max(0, (band - 24) / 24) * 55 * wob(Math.floor(t / 125))
  )
}

// The article's exact detector. Mutates `state` (advances the clock, the rolling
// average and the eased lava mix) and returns the instantaneous energy + whether
// the beat fired this step.
export function detect(state, dt) {
  state.t += dt
  const e = energy(state.t, state.band)
  const k = Math.pow(0.98, dt / 16.7) // decay normalized to 60fps frames
  state.rolling = state.rolling * k + e * (1 - k)
  const beat = e > state.rolling * state.threshold // the trigger line
  state.lava += ((beat ? 1 : 0) - state.lava) * 0.15 // the easing line the short is named after
  return { e, beat }
}

// Gradient sphere + energy-displaced ring. Pure w.r.t. the passed context.
export function drawPlanet(ctx, W, H, state, e, beat) {
  const cx = W / 2, cy = H / 2, R = H * 0.34
  ctx.fillStyle = "#0C0E10"
  ctx.fillRect(0, 0, W, H)
  ctx.strokeStyle = "#15181C"
  ctx.lineWidth = 1
  ctx.beginPath()
  for (let x = W / 6; x < W - 1; x += W / 6) { ctx.moveTo(x, 0); ctx.lineTo(x, H) }
  for (let y = H / 4; y < H - 1; y += H / 4) { ctx.moveTo(0, y); ctx.lineTo(W, y) }
  ctx.stroke()
  const g = ctx.createRadialGradient(cx - R * 0.35, cy - R * 0.4, R * 0.12, cx, cy, R)
  g.addColorStop(0, mix(EARTH[0], LAVA[0], state.lava))
  g.addColorStop(0.55, mix(EARTH[1], LAVA[1], state.lava))
  g.addColorStop(1, mix(EARTH[2], LAVA[2], state.lava))
  ctx.fillStyle = g
  ctx.beginPath()
  ctx.arc(cx, cy, R, 0, 7)
  ctx.fill()
  ctx.beginPath()
  const amp = (e / 255) * R * 0.16, seed = Math.floor(state.t / 90)
  for (let i = 0; i < 64; i++) {
    const a = (i / 64) * Math.PI * 2
    const r = R * 1.24 + amp * (wob(i * 3 + seed) - 0.5) * 2
    i
      ? ctx.lineTo(cx + Math.cos(a) * r, cy + Math.sin(a) * r)
      : ctx.moveTo(cx + Math.cos(a) * r, cy + Math.sin(a) * r)
  }
  ctx.closePath()
  ctx.strokeStyle = beat ? "rgba(255,77,0,.95)" : "rgba(255,77,0,.45)"
  ctx.lineWidth = 1.5
  ctx.stroke()
}

// Compute the canvas backing resolution from its CSS width (bounded: min 200px,
// 5:8 aspect, CSS-pixel resolution — no devicePixelRatio scaling).
export function canvasSize(canvas) {
  const cw = canvas.clientWidth || canvas.parentElement?.clientWidth || 0
  const w = Math.max(200, Math.round(cw))
  return { width: w, height: Math.round(w * 5 / 8) }
}

// One-liner -> params. Returns clamped { threshold, band } or null when the text
// doesn't match the planet.morph({ threshold: N, band: N }) shape.
export function parseOneliner(value) {
  const t = /threshold\s*:\s*([0-9.]+)/.exec(value)
  const b = /band\s*:\s*([0-9]+)/.exec(value)
  if (!t || !b) return null
  return {
    threshold: Math.min(THRESHOLD_MAX, Math.max(THRESHOLD_MIN, parseFloat(t[1]))),
    band: Math.min(BAND_MAX, Math.max(BAND_MIN, parseInt(b[1], 10))),
  }
}

// params -> one-liner echo.
export function formatOneliner(threshold, band) {
  return `planet.morph({ threshold: ${threshold.toFixed(2)}, band: ${band} })`
}
